import { Button } from '@bs/ui-components'
import { X } from 'lucide-react'
import { useEffect, type FormEvent, type ReactNode } from 'react'

/**
 * Renamed from MockEditDialog (2026-08-20): the dialog outlived the mock era — every caller now
 * submits a REAL save — but its default description still said "saved only in this browser",
 * which forced each caller to override it just to stop lying (the report dialog literally added
 * "it's sent for real" to fight it). No default now: a dialog says what its caller knows, or
 * nothing.
 */
export function EditDialog({
  title,
  description,
  submitLabel = 'Save changes',
  cancelLabel = 'Cancel',
  submitDisabled = false,
  onClose,
  onSubmit,
  children,
}: {
  title: string
  description?: string | undefined
  submitLabel?: string
  cancelLabel?: string
  /** For a save that's an in-flight network call (e.g. a real password change) — blocks a
   * double-submit rather than leaving the caller to race two requests against each other. */
  submitDisabled?: boolean
  onClose(): void
  onSubmit(): void
  children: ReactNode
}) {
  useEffect(() => {
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', closeOnEscape)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', closeOnEscape)
      document.body.style.overflow = ''
    }
  }, [onClose])

  function submit(event: FormEvent) {
    event.preventDefault()
    onSubmit()
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-primary/45 p-4 backdrop-blur-[2px]"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mock-dialog-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[20px] bg-white shadow-[0_24px_70px_rgba(0,7,79,0.25)]"
      >
        <header className="sticky top-0 z-10 flex items-start gap-4 border-b border-border bg-white px-6 py-5 sm:px-8">
          <div className="min-w-0 flex-1">
            <h2 id="mock-dialog-title" className="text-xl font-semibold text-primary">{title}</h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
          </div>
          <Button type="button" size="icon" variant="ghost" onClick={onClose} aria-label="Close">
            <X className="size-5" />
          </Button>
        </header>

        <div className="p-6 sm:p-8">{children}</div>

        <footer className="sticky bottom-0 flex justify-end gap-3 border-t border-border bg-white px-6 py-4 sm:px-8">
          <Button type="button" variant="ghost" onClick={onClose}>{cancelLabel}</Button>
          <Button type="submit" disabled={submitDisabled}>{submitLabel}</Button>
        </footer>
      </form>
    </div>
  )
}

export function DialogField({ label, children }: { label: string; children: ReactNode }) {
  return <label className="grid gap-2 text-sm font-semibold text-[#555]">{label}{children}</label>
}
